import type { TextEmbedder } from './types'

export const EMBEDDING_DIMENSIONS = 384

export function stableHash(text: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function hashToken(token: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < token.length; i++) {
    hash ^= token.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

function tokenizeForEmbedding(text: string): string[] {
  const normalized = text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
  if (!normalized) return []
  const words = normalized.split(/\s+/).filter(Boolean)
  const tokens = new Set(words)
  for (const word of words) {
    if (word.length <= 2) continue
    for (let i = 0; i <= word.length - 2; i++) {
      tokens.add(word.slice(i, i + 2))
    }
    for (let i = 0; i <= word.length - 3; i++) {
      tokens.add(word.slice(i, i + 3))
    }
  }
  return [...tokens]
}

export function normalizeEmbedding(vector: Float32Array): Float32Array {
  let norm = 0
  for (const value of vector) norm += value * value
  if (norm === 0) return vector
  const scale = 1 / Math.sqrt(norm)
  for (let i = 0; i < vector.length; i++) vector[i] *= scale
  return vector
}

export function hashEmbed(text: string): Float32Array {
  const vector = new Float32Array(EMBEDDING_DIMENSIONS)
  const tokens = tokenizeForEmbedding(text)
  for (const token of tokens) {
    const hash = hashToken(token)
    const index = hash % EMBEDDING_DIMENSIONS
    const sign = hash & 1 ? 1 : -1
    vector[index] += sign
  }
  return normalizeEmbedding(vector)
}

export function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0
  let dot = 0
  for (let i = 0; i < a.length; i++) dot += a[i] * b[i]
  return dot
}

export function createHashEmbedder(): TextEmbedder {
  return {
    name: 'hash-fallback',
    embed: hashEmbed,
    similarity: cosineSimilarity,
  }
}

export function createBrowserDefaultEmbedder(): TextEmbedder {
  return createHashEmbedder()
}

export async function embedText(embedder: TextEmbedder, text: string): Promise<Float32Array> {
  return await embedder.embed(text)
}

export function similarityFor(embedder: TextEmbedder, a: Float32Array, b: Float32Array): number {
  return embedder.similarity?.(a, b) ?? cosineSimilarity(a, b)
}
